/**
 * Display names for notification senders. Uses profiles the conversations and
 * Spaces providers already hold, and fetches any others once per session.
 */

import { useContext, useEffect, useMemo, useState } from 'react';
import { createApiClient, type PublicIdentity } from '@adieuu/shared';
import { useAppConfig } from '../config';
import { ConversationsContext } from './conversations/context';
import { SpacesContext } from './spaces/context';
import { mapPool } from '../utils/mapPool';

/** There is no batch profile endpoint, so a long inbox page must not open one request per sender at once. */
export const PROFILE_FETCH_CONCURRENCY = 4;

/** id -> name; `null` when the profile could not be loaded. */
const fetchedNames = new Map<string, string | null>();
const inflight = new Set<string>();

function profileName(profile: Pick<PublicIdentity, 'displayName' | 'username'> | undefined): string | undefined {
  return profile?.displayName ?? profile?.username ?? undefined;
}

/** Test hook: forget fetched names between cases. */
export function resetNotificationSenderNameCache(): void {
  fetchedNames.clear();
  inflight.clear();
}

export function useNotificationSenderNames(ids: readonly string[]): Record<string, string> {
  const conversationProfiles = useContext(ConversationsContext)?.participantProfiles;
  const spaceProfiles = useContext(SpacesContext)?.participantProfiles;
  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const [fetchEpoch, setFetchEpoch] = useState(0);
  const idsKey = [...new Set(ids)].sort().join(',');

  const names = useMemo(() => {
    const out: Record<string, string> = {};
    for (const id of idsKey ? idsKey.split(',') : []) {
      const name =
        profileName(conversationProfiles?.[id]) ??
        profileName(spaceProfiles?.[id]) ??
        fetchedNames.get(id) ??
        undefined;
      if (name) out[id] = name;
    }
    return out;
    // fetchEpoch re-reads the module cache after a fetch lands.
  }, [idsKey, conversationProfiles, spaceProfiles, fetchEpoch]);

  useEffect(() => {
    const missing = (idsKey ? idsKey.split(',') : []).filter(
      (id) => !(id in names) && !fetchedNames.has(id) && !inflight.has(id),
    );
    if (missing.length === 0) return;
    let cancelled = false;
    for (const id of missing) inflight.add(id);
    void mapPool(missing, PROFILE_FETCH_CONCURRENCY, async (id) => {
      try {
        const res = await api.identity.getProfile(id);
        fetchedNames.set(id, res.success && res.data ? (profileName(res.data) ?? null) : null);
      } catch {
        fetchedNames.set(id, null);
      } finally {
        inflight.delete(id);
      }
    }).then(() => {
      if (!cancelled) setFetchEpoch((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey, names, api]);

  return names;
}
